import { ui } from "@workspace/mobile";
import { Phone, Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import Loading from "../../../../../utils/Loading";
import { EDSButton } from "../../../../eds/EDSButton";
import Table from "../../../../Shared/Table/Table";
import {
  deleteRowFromData,
  GroupMemberData,
  newGuest,
  revertChangedRow,
  saveGuests,
  updateRow
} from "./SelectedBookingGuestsController";

const SelectedBookingGuests = () => {
  const { selectedBookingV4, setDiaryContext } = useDiaryContext();
  const bookingId = selectedBookingV4?._id;
  const groupMembers = selectedBookingV4?.group_members?.slice() ?? [];
  const [data, setData] = useState<GroupMemberData[]>(
    [...groupMembers].map((d) => ({ ...d }))
  );
  // filter out deleted rows as they can be hidden from this array at this point.
  const guestCount = data.filter((d) => !d.deleted).length;
  const heading = `Guests ${guestCount > 0 ? `(${guestCount})` : ""}`;

  useEffect(() => {
    // yes the map, slice and [...] is overkill but I was having array reference issues and this combination of insanity works.
    // if you refactor, test edit of multiple rows, watch out for all rows being updated on first save and not being able to cancel outstanding edits.
    setData([...groupMembers].map((d) => ({ ...d })));
  }, [bookingId]);

  return (
    <div className="BookingGuests mt-panel-padding flex flex-wrap">
      <div className="BookingGuests-Heading font-bold m-panel-padding text-base">
        {heading}
      </div>
      <div className="BookingGuests-Content mt-panel-padding flex-[1_1_100%] flex flex-col">
        <Table
          columns={[
            {
              dataName: "first_name",
              caption: "First Name",
              editable: true
            },
            {
              dataName: "last_name",
              caption: "Last Name",
              editable: true
            },
            {
              dataName: "email",
              caption: "Email",
              editable: true,
              type: "email"
            },
            {
              dataName: "phone",
              caption: "Phone",
              leftIcon: (
                <Phone
                  size={16}
                  className="PhoneIcon mr-1.5 text-muted-foreground"
                />
              ),
              editable: true,
              type: "tel"
            }
          ]}
          data={data}
          newRowFlag="add"
          editButton={{
            visible: true,
            onConfirm: async (e, row) => {
              if (
                row.add &&
                row.first_name === "" &&
                row.last_name === "" &&
                row.email === "" &&
                row.phone === ""
              ) {
                // row is a blank new row - block save and row locking until something is entered
                return { disabledEdit: false };
              }

              const response = await saveGuests(
                row.add ? "add" : "edit",
                selectedBookingV4,
                row
              );

              if (!response) {
                // error
                return { disabledEdit: false };
              }

              setDiaryContext({
                selectedBookingV4: {
                  ...selectedBookingV4,
                  ...response
                }
              });

              // Update local data state to reflect the new guest ID and remove the 'add' flag
              if (row.add) {
                setData((prevData) =>
                  prevData.map((item) =>
                    item.id === row.id ? { ...row, add: undefined } : item
                  )
                );
              }

              return { disabledEdit: true };
            },
            onCancel: (e, row) => {
              if (row.add) {
                // Edit cancelled row is a new row not yet saved, no need to save so just remove from array.
                setData(deleteRowFromData(data, row));
                // Don't disable the edit, otherwise any new rows added after will take its position and not be in an editable state.
                return { disabledEdit: false };
              }

              // if row is not new, revert it
              const revertedRow = revertChangedRow(selectedBookingV4, row);
              setData(updateRow(data, revertedRow ?? row));
            }
          }}
          deleteButton={{
            visible: true,
            onClick: async (e, row) => {
              const confirmDelete = await ui.displayConfirm({
                title: "Delete Guest?",
                message: `Are you sure you wish to remove ${row.first_name} ${row.last_name} from this booking?`,
                accept: "Confirm",
                cancel: "Cancel"
              });

              if (confirmDelete) {
                const response = await saveGuests(
                  "delete",
                  selectedBookingV4,
                  row
                );

                if (!response) {
                  // error
                  return { deleteRow: false };
                }

                setDiaryContext({
                  selectedBookingV4: {
                    ...selectedBookingV4,
                    ...response
                  }
                });
              }
              // by returning deleteRow:true the table will hide the row, this is because if we update the data and re-render the table, the edit state for any rows below become misaligned.
              return { deleteRow: confirmDelete };
            }
          }}
        />
        {!selectedBookingV4 && <Loading />}
        {guestCount === 0 && selectedBookingV4 && (
          <span className="NoRecords p-panel-padding m-auto">
            No guests have been added to this booking.
          </span>
        )}
        {selectedBookingV4 && (
          <EDSButton
            variant="default"
            size="md"
            className="Guest-AddButton mt-panel-padding ml-panel-padding w-auto self-start"
            onClick={() => {
              setData([...data, newGuest()]);
            }}
          >
            <Plus size={16} />
            Add New
          </EDSButton>
        )}
      </div>
    </div>
  );
};

export default SelectedBookingGuests;
